import EVENTS from "../lib/events.js"
import srraf from "srraf"

class HeaderGroup {
  #previousImpactLogoVisibility = false

  constructor() {
    // keep running srraf after it passes the bottom of the impact logo by this amount
    this.overscrollThreshold = 100

    this.selectors = {
      announcementBar: "announcement-bar",
      header: "header-wrapper",
      impactLogo: "impact-logo",
    }

    this.init()
  }

  init() {
    // setup the event handling for it all
    this.announcementBar = document.querySelector(
      this.selectors.announcementBar,
    )
    this.header = document.querySelector(this.selectors.header)
    this.impactLogo = document.querySelector(this.selectors.impactLogo)

    // if we don't have a header, we don't need to do anything
    if (!this.header) return
    // if impact logo isn't present, we stick the header immediately
    if (!this.impactLogo) {
      this.header.setAttribute("data-reveal-logo", true)
    }

    // Theme Editor: sectionIds to watch changes
    const ids = [
      this.header.dataset.sectionId,
      this.announcementBar?.dataset.sectionId,
      this.impactLogo?.dataset.sectionId,
    ]
      .filter(Boolean)
      .map((id) => id.replace("shopify-section-", ""))

    this.controller = new AbortController()
    const eventOptions = { signal: this.controller.signal }

    // Theme Editor: watch for section load events
    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_LOAD,
      (event) => {
        this.controller.abort()
        this.watcher?.destroy()
        this.init()
      },
      eventOptions,
    )

    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_UNLOAD,
      (event) => {
        if (event?.detail?.sectionId && ids.includes(event.detail.sectionId)) {
          this.controller.abort()
          this.watcher?.destroy()
          setTimeout(() => {
            this.init()
          }, 100)
        }
      },
      eventOptions,
    )

    this.announcementBarBox = this.announcementBar?.getBoundingClientRect()
    this.headerBox = this.header?.getBoundingClientRect()
    this.impactLogoBox = this.impactLogo?.getBoundingClientRect()

    // We need to update the *Box bottom values with the window scrollY on load
    this.announcementBarBoxBottom =
      window.scrollY + (this.announcementBarBox?.bottom || 0)
    this.headerBoxBottom = window.scrollY + (this.headerBox?.bottom || 0)
    this.impactLogoBoxBottom =
      window.scrollY + (this.impactLogoBox?.bottom || 0)

    this.instantSticky = this.header.dataset.stickyHeaderStyle === "instant"

    const maxHeightToWatch = this.impactLogoBoxbottom + this.overscrollThreshold
    this.watcher = srraf(({ y, py }) => {
      if (maxHeightToWatch < y) return
      if (y === py) return

      this.#handleScroll(y)
    })
    this.#handleScroll(window.scrollY)
  }

  #handleScroll(y) {
    if (this.instantSticky) {
      const stuckHeaderHeight =
        this.headerBox.height + (this.announcementBarBox?.height || 0)
      // Set visibilities of the components
      this.announcementBar?.setAttribute(
        "data-is-visible",
        y < this.announcementBarBoxBottom,
      )
      this.header?.setAttribute("data-is-visible", y < this.headerBoxBottom)
      this.impactLogo?.setAttribute(
        "data-is-visible",
        y < this.impactLogoBoxBottom - stuckHeaderHeight,
      )

      if (y > 0) {
        this.header.setAttribute("data-is-sticky", true)

        // Impact logo visibility change
        if (
          this.impactLogo &&
          this.impactLogo.getAttribute("data-is-visible") !==
            this.#previousImpactLogoVisibility
        ) {
          this.#previousImpactLogoVisibility =
            this.impactLogo.getAttribute("data-is-visible")

          const revealLogo = y > this.impactLogoBoxBottom - stuckHeaderHeight
          this.header.setAttribute("data-reveal-logo", revealLogo)
        } else {
          this.header.setAttribute("data-is-sticky", true)
        }
      } else {
        this.header.setAttribute("data-is-sticky", false)
      }
    } else {
      // Set visibilities of the components

      if (this.announcementBar) {
        this.announcementBar.setAttribute(
          "data-is-visible",
          y < this.announcementBarBoxBottom,
        )

        // if it's a delayed sticky, we set the translateY to an inverse of the height of the header
        // then when the impact logo is out of view, we can transition the translateY to 0
        if (y > this.announcementBarBoxBottom) {
          this.announcementBar.parentNode.style.setProperty(
            "--announcement-bar-translate-y",
            `-${this.headerBox.height}px`,
          )
        } else {
          this.announcementBar.parentNode.style.setProperty(
            "--announcement-bar-translate-y",
            0,
          )
        }
      }

      if (this.header) {
        this.header?.setAttribute("data-is-visible", y < this.headerBoxBottom)

        // if it's a delayed sticky, we set the translateY to an inverse of the height of the header
        // then when the impact logo is out of view, we can transition the translateY to 0
        if (y > this.headerBoxBottom) {
          this.header.parentNode.style.setProperty(
            "--header-translate-y",
            `-${this.headerBox.height}px`,
          )
          this.header.setAttribute("data-reveal-logo", true)
        } else {
          if (!this.impactLogo) {
            this.header.setAttribute("data-is-sticky", y > 0)
            this.header.setAttribute("data-reveal-logo", true)
          } else {
            this.header.setAttribute("data-reveal-logo", false)
          }

          this.header.parentNode.style.setProperty("--header-translate-y", 0)
        }
      }

      if (this.impactLogo) {
        this.impactLogo.setAttribute(
          "data-is-visible",
          y < this.impactLogoBoxBottom,
        )

        if (
          this.impactLogo?.getAttribute("data-is-visible") !==
          this.#previousImpactLogoVisibility
        ) {
          this.#previousImpactLogoVisibility =
            this.impactLogo?.getAttribute("data-is-visible")

          if (y > this.impactLogoBoxBottom) {
            this.header.setAttribute("data-is-sticky", true)
          } else {
            this.header.setAttribute("data-is-sticky", false)
          }
        }
      }
    }
  }
}

window.addEventListener("DOMContentLoaded", () => new HeaderGroup())
